/**
 * RestaurantDetailDialog — full per-concept page (one of the 11 dining
 * concepts on the property). Shows:
 *   • Logo + accent-colored header
 *   • Cuisine, capacity, opening hours (one-liners)
 *   • Long-form description
 *   • Menu samples (PDFs / images) gallery
 *   • Photos + Renders galleries
 *   • Team members (free-form names)
 * The dialog is read-only for viewers and editable for admin/engineer
 * (matches the rest of the property-details flow).
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Save, Wand2, Users, Clock, ChefHat, Image as ImageIcon, CalendarDays, Map as MapIcon, ChevronLeft, ChevronRight, Upload, X, Printer, FileText, Package, Boxes } from 'lucide-react';
import MediaTile from './MediaTile';
import { printHtml } from '../lib/print';
import DepartmentScheduleDialog from './DepartmentScheduleDialog';
import DepartmentRoadmapDialog from './DepartmentRoadmapDialog';
import RestaurantSavedFiles from './RestaurantSavedFiles';
import InventoryDialog from './InventoryDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function abs(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND}${url}`;
}

function esc(v) {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default function RestaurantDetailDialog({ outlet, open, onClose, canEdit, onUpdated }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null); // edited copy when canEdit
  const [saving, setSaving] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  // Mounts the InventoryTable for `restaurant.{outlet.id}` — same node
  // surfaced under F&B workspace and the aggregated OS&E Lists view.
  const [oseOpen, setOseOpen] = useState(false);
  const [uploadingMenu, setUploadingMenu] = useState(false);
  const menuInputRef = useRef(null);

  useEffect(() => { setHeroIdx(0); setDraft(null); }, [outlet?.id]);

  if (!outlet) return null;
  const current = draft || outlet;
  const accent = current.accent_color || '#003DA5';
  // Hero slideshow prefers renders (the "concept reveal") → photos.
  const heroSlides = [...(current.renders || []), ...(current.photos || [])];
  const hero = heroSlides[heroIdx] || null;

  // Stable dept-scope so the AI Schedule + Roadmap stored against this
  // restaurant are mirrored everywhere the dialog is opened (landing page,
  // F&B workspace, FOH workspace).
  const restaurantScope = `restaurant.${outlet.id}`;
  const scopeLabel = `${outlet.name} (Restaurant)`;

  const regenerateLogo = async () => {
    if (!canEdit) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/property/fnb/${outlet.id}/generate-logo`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      toast.success('Logo regenerated.');
      onUpdated?.({ ...current, logo_url: data.logo_url, accent_color: data.accent_color });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Logo regeneration failed');
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = () => setDraft({ ...outlet, team_members: outlet.team_members || [] });
  const cancelEdit = () => setDraft(null);

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Patch this outlet within the full property doc.
      const { data: prop } = await axios.get(`${API}/property`);
      const fnb = (prop.fnb || []).map((o) => (o.id === outlet.id ? { ...o, ...draft } : o));
      const { data } = await axios.put(`${API}/admin/property`, { ...prop, fnb }, { headers: { Authorization: `Bearer ${adminToken}` } });
      const next = (data.fnb || []).find((o) => o.id === outlet.id);
      toast.success('Restaurant details saved.');
      setDraft(null);
      onUpdated?.(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const printPage = () => {
    const o = current;
    const team = (o.team_members || []).map((n) => `<li>${esc(n)}</li>`).join('');
    const menus = (o.menu_samples || []).map((u) => `<li>${esc((u || '').split('/').pop())}</li>`).join('');
    const html = `
      ${o.logo_url ? `<img src="${esc(abs(o.logo_url))}" style="max-height:48mm;margin:0 auto 6mm;display:block"/>` : ''}
      <p class="kicker" style="color:${esc(accent)}">DREAMS ROSE HALL · DINING</p>
      <h1 style="color:${esc(accent)}">${esc(o.name)}</h1>
      ${o.detail ? `<p><strong>${esc(o.detail)}</strong></p>` : ''}
      <div class="grid">
        ${o.cuisine ? `<p><strong>Cuisine:</strong> ${esc(o.cuisine)}</p>` : ''}
        ${o.capacity ? `<p><strong>Capacity:</strong> ${esc(o.capacity)}</p>` : ''}
        ${o.hours ? `<p><strong>Hours:</strong> ${esc(o.hours)}</p>` : ''}
      </div>
      ${o.description ? `<h2>Concept</h2><p>${esc(o.description).replace(/\n/g, '<br/>')}</p>` : ''}
      ${team ? `<h2>Team</h2><ul>${team}</ul>` : ''}
      ${menus ? `<h2>Menus on file</h2><ul>${menus}</ul>` : ''}
    `;
    printHtml(html, `${o.name} — Dreams Rose Hall`);
  };

  // Upload a menu file (PDF or image) directly while editing. We save
  // through the same property PUT so it persists immediately — the user
  // doesn't have to click "Save" first.
  const uploadMenu = async (file) => {
    if (!file || !canEdit) return;
    setUploadingMenu(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: up } = await axios.post(`${API}/admin/upload`, fd, { headers: { Authorization: `Bearer ${adminToken}` } });
      const url = up?.url || up?.file_url || up?.path;
      if (!url) throw new Error('Upload returned no URL');
      const next = { ...current, menu_samples: [...(current.menu_samples || []), url] };
      if (draft) setDraft(next); else await persistOutlet(next);
      toast.success('Menu uploaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Menu upload failed');
    } finally {
      setUploadingMenu(false);
      if (menuInputRef.current) menuInputRef.current.value = '';
    }
  };

  const removeMenu = async (idx) => {
    if (!canEdit) return;
    const next = { ...current, menu_samples: (current.menu_samples || []).filter((_, i) => i !== idx) };
    if (draft) setDraft(next); else await persistOutlet(next);
  };

  // Save a single outlet patch through the property PUT — used by upload/remove
  // menu when we're NOT in full edit mode so changes land immediately.
  const persistOutlet = async (patched) => {
    const { data: prop } = await axios.get(`${API}/property`);
    const fnb = (prop.fnb || []).map((o) => (o.id === outlet.id ? { ...o, ...patched } : o));
    const { data } = await axios.put(`${API}/admin/property`, { ...prop, fnb }, { headers: { Authorization: `Bearer ${adminToken}` } });
    const next = (data.fnb || []).find((o) => o.id === outlet.id);
    onUpdated?.(next);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && (saving ? null : (cancelEdit(), onClose?.()))}>
      <DialogContent className="max-w-5xl max-h-[94vh] w-[95vw] sm:w-auto overflow-hidden flex flex-col p-3 sm:p-6" data-testid={`restaurant-detail-${outlet.id}`} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 sm:gap-3 pr-6" style={{ fontFamily: 'Manrope' }}>
            {current.logo_url && (
              <img
                src={abs(current.logo_url)}
                alt={`${current.name} logo`}
                data-testid={`restaurant-logo-${outlet.id}`}
                className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl object-contain flex-shrink-0"
                style={{ background: 'white', border: `2px solid ${accent}30`, padding: 2 }}
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Dining concept</p>
              <h2 className="text-base sm:text-xl font-bold truncate" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>{current.name}</h2>
            </div>
            <button
              type="button"
              onClick={printPage}
              data-testid={`restaurant-print-${outlet.id}`}
              className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold text-white flex-shrink-0"
              style={{ backgroundColor: accent, fontFamily: 'Manrope' }}
            >
              <Printer className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">Print</span>
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 py-3">
          {/* Hero carousel — renders take priority over photos, mirroring
              the public DiningConcept popup. Hidden when nothing to show. */}
          {hero && (
            <div
              data-testid={`restaurant-hero-${outlet.id}`}
              className="relative w-full overflow-hidden rounded-xl"
              style={{ aspectRatio: '16 / 7', backgroundColor: `${accent}10` }}
            >
              <img
                src={abs(hero)}
                alt={`${current.name} hero`}
                className="w-full h-full object-cover"
              />
              {heroSlides.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setHeroIdx((i) => (i - 1 + heroSlides.length) % heroSlides.length)}
                    aria-label="Previous"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeroIdx((i) => (i + 1) % heroSlides.length)}
                    aria-label="Next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <p className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-bold" style={{ fontFamily: 'Manrope' }}>
                    {heroIdx + 1} / {heroSlides.length}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Quick action bar — every restaurant carries its own AI Schedule
              + Roadmap. The dept scope is `restaurant.{id}` so opening from
              the landing page or from F&B/FOH workspace shows the same data
              (mirroring). */}
          {adminToken && (
            <div className="flex flex-wrap items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <button
                type="button"
                data-testid={`restaurant-schedule-btn-${outlet.id}`}
                onClick={() => setScheduleOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white transition hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}
              >
                <CalendarDays className="w-3.5 h-3.5" /> AI Schedule
              </button>
              <button
                type="button"
                data-testid={`restaurant-roadmap-btn-${outlet.id}`}
                onClick={() => setRoadmapOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white transition hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #15803D, #22C55E)' }}
              >
                <MapIcon className="w-3.5 h-3.5" /> Roadmap
              </button>
              {/* OS&E & Inventories — opens the per-restaurant inventory
                  list inside a standalone dialog. The same data is also
                  exposed under the F&B workspace and on the public
                  "OS&E Lists" aggregated view. */}
              <button
                type="button"
                data-testid={`restaurant-ose-btn-${outlet.id}`}
                onClick={() => setOseOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white transition hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #003DA5, #1F4FBE)' }}
                title="Operating supplies & equipment for this restaurant"
              >
                <Package className="w-3.5 h-3.5" /> OS&amp;E
              </button>
              <button
                type="button"
                data-testid={`restaurant-inventories-btn-${outlet.id}`}
                onClick={() => setOseOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold text-white transition hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #B45309, #D97706)' }}
                title="Inventories — current stock & ordering"
              >
                <Boxes className="w-3.5 h-3.5" /> Inventories
              </button>
            </div>
          )}

          {/* Quick facts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" style={{ fontFamily: 'Manrope' }}>
            <Fact icon={<ChefHat className="w-3.5 h-3.5" />} label="Cuisine" value={current.cuisine} accent={accent}
              editing={!!draft} onChange={(v) => setDraft((d) => ({ ...d, cuisine: v }))} />
            <Fact icon={<Users className="w-3.5 h-3.5" />} label="Capacity" value={current.capacity} accent={accent}
              editing={!!draft} onChange={(v) => setDraft((d) => ({ ...d, capacity: v }))} />
            <Fact icon={<Clock className="w-3.5 h-3.5" />} label="Opening hours" value={current.hours} accent={accent}
              editing={!!draft} onChange={(v) => setDraft((d) => ({ ...d, hours: v }))} />
          </div>

          {/* Description / Concept */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: accent, fontFamily: 'Manrope' }}>Concept</h3>
            {draft ? (
              <textarea
                rows={4}
                data-testid="restaurant-description-input"
                value={draft.description || ''}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none"
                style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
              />
            ) : (
              <p className="text-sm text-gray-700 whitespace-pre-wrap" style={{ fontFamily: 'Manrope' }}>
                {current.description || <em className="text-gray-400">No concept description yet.</em>}
              </p>
            )}
          </section>

          {/* Team members */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1" style={{ color: accent, fontFamily: 'Manrope' }}>
              <Users className="w-3 h-3" /> Team members
            </h3>
            {draft ? (
              <TeamEditor
                value={draft.team_members || []}
                onChange={(v) => setDraft((d) => ({ ...d, team_members: v }))}
              />
            ) : (
              <ul className="text-sm text-gray-700 space-y-0.5">
                {(current.team_members || []).length === 0 && <em className="text-gray-400">No team members listed.</em>}
                {(current.team_members || []).map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            )}
          </section>

          {/* Menus — galleries are always shown when there are samples.
              In edit mode an Upload control + Remove buttons appear. */}
          {/* iter-80 — Menus section is ALWAYS visible (was previously
              gated on `menu_samples.length > 0 || canEdit`). Even with
              no menus on file, visitors should see the section so they
              know menus are coming, and editors see the upload affordance
              without having to flip a switch first. */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1" style={{ color: accent, fontFamily: 'Manrope' }}>
                <FileText className="w-3 h-3" /> Menus
              </h3>
              {canEdit && (
                <>
                  <input
                    ref={menuInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    data-testid={`restaurant-menu-input-${outlet.id}`}
                    onChange={(e) => uploadMenu(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => menuInputRef.current?.click()}
                    disabled={uploadingMenu}
                    data-testid={`restaurant-menu-upload-${outlet.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold disabled:opacity-50"
                    style={{ backgroundColor: `${accent}20`, color: accent, fontFamily: 'Manrope' }}
                  >
                    {uploadingMenu ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Upload menu
                  </button>
                </>
              )}
            </div>
            {(current.menu_samples || []).length === 0 ? (
              <p className="text-xs text-center py-4 rounded-lg" style={{ backgroundColor: `${accent}10`, color: '#7A8C8C', fontFamily: 'Manrope' }}>
                {canEdit ? 'No menus on file yet — upload a PDF or photo to share with guests.' : 'Menus will be added here once the new concept is finalised.'}
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {current.menu_samples.map((u, i) => (
                  <div key={i} className="relative aspect-[3/4] rounded-lg overflow-hidden bg-slate-100 group">
                    <MediaTile url={abs(u)} caption={(u || '').split('/').pop()} />
                    {canEdit && (
                      <button
                        type="button"
                        data-testid={`restaurant-menu-remove-${outlet.id}-${i}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeMenu(i); }}
                        title="Remove menu"
                        className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Photos */}
          {(current.photos || []).length > 0 && (
            <section>
              <h3 className="text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1" style={{ color: accent, fontFamily: 'Manrope' }}>
                <ImageIcon className="w-3 h-3" /> Photos
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {current.photos.map((u, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <MediaTile url={abs(u)} caption={`Photo ${i + 1}`} />
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Saved Files for this restaurant — schedules, OS&E lists,
              checklists, etc. authored while inside this dialog get saved
              with owner_node_id = `restaurant.{outlet.id}` and surface
              here automatically. */}
          <RestaurantSavedFiles outlet={current} accentColor={accent} />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-gray-200">
          {canEdit && !draft && (
            <button type="button" onClick={regenerateLogo} disabled={generating} data-testid={`restaurant-regenerate-logo-${outlet.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', fontFamily: 'Manrope' }}>
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Regenerate logo
            </button>
          )}
          {canEdit && !draft && (
            <button type="button" onClick={startEdit} data-testid={`restaurant-edit-${outlet.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold"
              style={{ backgroundColor: `${accent}20`, color: accent, fontFamily: 'Manrope' }}>
              Edit details
            </button>
          )}
          {canEdit && draft && (
            <>
              <button type="button" onClick={cancelEdit} disabled={saving}
                className="px-3 py-2 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100"
                style={{ fontFamily: 'Manrope' }}>Cancel</button>
              <button type="button" onClick={saveDraft} disabled={saving} data-testid={`restaurant-save-${outlet.id}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: accent, fontFamily: 'Manrope' }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </>
          )}
        </div>
      </DialogContent>
      {/* Schedule + Roadmap dialogs — scoped to `restaurant.{id}` so the
          same restaurant opened from landing / F&B / FOH all see the same
          rosters, schedules and milestones. */}
      <DepartmentScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        deptId={restaurantScope}
        deptLabel={scopeLabel}
      />
      <DepartmentRoadmapDialog
        open={roadmapOpen}
        onClose={() => setRoadmapOpen(false)}
        deptId={restaurantScope}
        deptLabel={scopeLabel}
        canEdit={canEdit}
      />
      {/* OS&E / Inventories — the same node is also exposed under
          F&B workspace and the aggregated OS&E Lists landing tile. */}
      <InventoryDialog
        open={oseOpen}
        onClose={() => setOseOpen(false)}
        nodeId={restaurantScope}
        nodeLabel={scopeLabel}
        headerAccent={accent}
      />
    </Dialog>
  );
}

function Fact({ icon, label, value, accent, editing, onChange }) {
  return (
    <div className="rounded-lg px-3 py-2" style={{ backgroundColor: `${accent}10`, border: `1px solid ${accent}25` }}>
      <p className="text-[9px] uppercase tracking-wider font-bold flex items-center gap-1" style={{ color: accent }}>
        {icon} {label}
      </p>
      {editing ? (
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-1 px-2 py-1 rounded text-sm bg-white outline-none border"
          style={{ borderColor: `${accent}40` }}
        />
      ) : (
        <p className="text-sm font-bold mt-0.5" style={{ color: '#1A2E2E' }}>
          {value || <em className="text-gray-400 font-normal">Not set</em>}
        </p>
      )}
    </div>
  );
}

function TeamEditor({ value, onChange }) {
  const list = value || [];
  const add = () => onChange([...list, '']);
  const set = (i, v) => onChange(list.map((x, j) => (j === i ? v : x)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  return (
    <div className="space-y-1">
      {list.map((n, i) => (
        <div key={i} className="flex items-center gap-1">
          <input
            value={n}
            onChange={(e) => set(i, e.target.value)}
            placeholder="Name · Role"
            className="flex-1 px-2 py-1 rounded text-xs border outline-none"
            style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
          />
          <button type="button" onClick={() => remove(i)} className="px-2 py-1 text-xs text-red-600">×</button>
        </div>
      ))}
      <button type="button" onClick={add} className="text-[10px] font-bold uppercase tracking-wider text-blue-700">+ Add member</button>
    </div>
  );
}
